import { MapStoreMunicipality } from '../shared/models/municipality/Municipality';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, Subject, Observable, of } from 'rxjs';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { HeaderFooterVisibilityService } from '@app/shared/services/header-footer-visibility.service';
import { MapStoreService } from '@app/energy-map/services/map-store.service';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { MeterDataStoreServiceDirective } from '@app/shared/services/store/meter-data-store.service';
import { LocalStorageService } from '@app/shared/services/storage/local-storage.service';
import { ReleaseNotesService } from '@app/shared/services/api/release-notes.service';
import { forEach, orderBy, sortBy } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { ReleaseNoteDialogComponent } from '@app/release-note-admin/dialog/release-note-dialog/release-note-dialog.component';
import { UserRegion } from '@app/shared/services/api/user-regions.service';
import { DashoardProperties } from '@app/shared/models/AppInsights';
import { TitleCasePipe } from '@angular/common';
import { EventsService } from '@app/shared/services/events.service';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { environment } from '@env/environment';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { VersionCheckService } from '@app/shared/services/version-check.service';
import { VersionCheckDialogComponent } from '../version-check/dialog/version-check-dialog/version-check-dialog.component';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  providers: [TitleCasePipe],
  encapsulation: ViewEncapsulation.None,
})
export class MainComponent implements OnDestroy, OnInit {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public routerSubscription: Subscription = new Subscription();
  public isHeaderVisible = true;
  public isFooterVisible = true;
  public isMap = false;
  private subs1: Subscription = new Subscription();

  constructor(
    public appInsightsServiceDirective: AppInsightsServiceDirective,
    public authService: AuthService,
    public headerFooterVisibilityService: HeaderFooterVisibilityService,
    public mapStoreService: MapStoreService,
    public router: Router,
    public matDialog: MatDialog,
    private readonly userMunicipalitiesService: UserMunicipalitiesService,
    private readonly meterDataStoreServiceDirective: MeterDataStoreServiceDirective,
    private readonly localStorageService: LocalStorageService,
    private readonly releaseNotesService: ReleaseNotesService,
    private readonly titleCasePipe: TitleCasePipe,
    private readonly eventsService: EventsService,
    private readonly idle: Idle,
    private readonly keepalive: Keepalive,
    private readonly versionCheckService: VersionCheckService,
    private readonly cookieService: CookieService,
  ) {
    if (environment.idleWatch) {
      // sets an idle timeout of 5 seconds, for testing purposes.
      this.idle.setIdle(parseInt(environment.idleInSeconds, 10));
      // sets a timeout period of 5 seconds. after 10 seconds of inactivity, the user will be considered timed out.
      this.idle.setTimeout(parseInt(environment.idleTimeoutInSeconds, 10));
      // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
      this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

      this.idle.onIdleEnd.subscribe(() => {
        this.idle.watch();
      });

      this.idle.onTimeout.subscribe(() => {
        this.matDialog
          .open(NotificationDialogComponent, {
            data: {
              descriptionText: `Sie waren abwesend und Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.`,
              orientation: 'row',
              alignment: 'start',
            },
            panelClass: ['-sm'],
          })
          .afterClosed()
          .subscribe((a) => {
            this.authService.logout();
          });
      });

      this.idle.onIdleStart.subscribe();

      this.idle.onTimeoutWarning.subscribe();

      // sets the ping interval to 15 seconds
      this.keepalive.interval(parseInt(environment.idlePingIntervalInSeconds, 10));

      this.idle.watch();
    }
  }

  public ngOnInit(): void {
    this.isMap = this.router.url.indexOf('/energiekarte') >= 0 || this.router.url.indexOf('/vertriebstool') >= 0;
    this.routerSubscription = this.router.events.subscribe((event) => {
      this.navigationInterceptor(event);
    });
    this.headerFooterVisibilityService.isHeaderVisible$.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => (this.isHeaderVisible = value));
    this.headerFooterVisibilityService.isFooterVisible$.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => (this.isFooterVisible = value));
    this.subs1 = this.userMunicipalitiesService.regions$.pipe(takeUntil(this.unsubscribe$)).subscribe((userRegions: UserRegion[]) => {
      const _sortedData = sortBy(userRegions, ['regionName']);
      const regions = userRegions.length > 0 ? _sortedData.map((e) => e.regionName).join(',') : '';

      if (this.authService.currentUser) {
        this.appInsightsServiceDirective.addProperties({
          SessionId: this.authService.sessionId,
          UserType: this.authService.currentUser.roleName,
          Regions: regions,
        });
      }
    });

    this.meterDataStoreServiceDirective.preloadReferences();

    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((municipality: MapStoreMunicipality) => {
        this.logMunicipality(municipality);
        if (!this.router.url.includes('grid-infra')) {
          this.router.navigateByUrl(this.router.url);

          this.eventsService.publishEvent('municipalityChangedEvent', {
            municipalityKey: municipality.key,
            municipalityName: municipality.name,
            municipalityGuid: municipality.guid,
          });
        }
      });

    const releaseNotes = this.localStorageService.getItem('releaseNotes');

    if (releaseNotes === '1') {
      this.triggerReleaseNotesDialog().subscribe(() => {
        this.localStorageService.setItem('releaseNotes', '0');
        this.initVersionCheck();
      });
    } else {
      this.initVersionCheck();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.subs1) {
      this.subs1.unsubscribe();
    }
  }

  public logMunicipality(municipality: any) {
    if (municipality) {
      this.appInsightsServiceDirective.addProperties({
        MunicipalityKey: municipality.key,
        MunicipalityName: municipality.name,
      });

      this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.municipalityActive);

      const link = this.titleCasePipe.transform(decodeURI(window.location.pathname.substr(window.location.pathname.lastIndexOf('/') + 1)));
      if (link !== '' && link.toLowerCase() !== 'instrumententafel') {
        this.appInsightsServiceDirective.logEvent<DashoardProperties>(ApplicationInsightEvent.clickDashboard, { Link: link });
      }
    }
  }

  public navigationInterceptor(event: any) {
    if (event instanceof NavigationEnd) {
      this.isMap = this.router.url.indexOf('/energiekarte') >= 0 || this.router.url.indexOf('/vertriebstool') >= 0;
    }
  }

  private triggerReleaseNotesDialog(): Observable<void> {
    return this.releaseNotesService.getLatest(this.authService.currentUser.lastLogin).pipe(
      map((data: any) => {
        forEach(data, (item) => (item['notesList'] = item.notes.split('\n')));
        return orderBy(data, ['releaseDate'], ['desc']);
      }),
      switchMap((data: any[]) => {
        if (data.length > 0) {
          const dialogRef = this.matDialog.open(ReleaseNoteDialogComponent, {
            data: {
              releaseItems: data,
              title: '',
            },
            panelClass: '-md',
          });
          return dialogRef.afterClosed().pipe(
            takeUntil(this.unsubscribe$),
            map(() => void 0),
          );
        } else {
          return of(void 0);
        }
      }),
      takeUntil(this.unsubscribe$),
    );
  }

  private initVersionCheck(): void {
    const VERSION_KEY = 'app_version';
    const storedVersion = this.cookieService.get(VERSION_KEY)?.trim();
    this.versionCheckService.loadCurrentVersion().subscribe((serverVersion: string) => {
      const newestVersion = serverVersion?.trim();
      if (!newestVersion) return;

      if (!storedVersion) {
        this.cookieService.set(VERSION_KEY, newestVersion);
        return;
      }

      if (storedVersion === newestVersion) {
        return;
      }

      this.cookieService.set(VERSION_KEY, newestVersion);
      this.matDialog.open(VersionCheckDialogComponent, {
        width: '604px',
        panelClass: '-md',
        disableClose: true,
        autoFocus: 'dialog',
        restoreFocus: true,
      });
    });
  }
}
